/**
 * Chart data fetching — debounced, abortable, and never out of order.
 *
 * Two failure modes this exists to prevent, both of which look like a bug in
 * the chart rather than in the fetching:
 *
 *   - A drag on the zoom slider fires a range change per animation frame. Each
 *     one is a query that decodes segment blobs. Without debouncing, one drag
 *     queues dozens of them and the server does real work for every frame the
 *     user passed through.
 *
 *   - Responses can land out of order. A wide range takes longer than the
 *     narrow one requested after it, so the stale wide response arrives last
 *     and overwrites the correct data. Every request carries a sequence number
 *     and anything not from the latest is discarded, in addition to aborting.
 */
import { useEffect, useRef, useState } from "react";

import { ApiError, createClient, type ChartResponse } from "@ch/client";

const client = createClient();

export type ChartRange = { from: number; to: number };

export type ChartState = {
    data: ChartResponse | null;
    loading: boolean;
    error: string | null;
};

export function useChartData(
    deviceId: string | null,
    range: ChartRange,
    debounceMs = 250,
): ChartState {
    const [state, setState] = useState<ChartState>({
        data: null,
        loading: false,
        error: null,
    });

    // Monotonic request id. Compared on arrival so a slow earlier response
    // cannot overwrite a fast later one.
    const latest = useRef(0);

    useEffect(() => {
        if (!deviceId) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        const requestId = ++latest.current;
        const controller = new AbortController();

        setState((previous) => ({ ...previous, loading: true }));

        const timer = setTimeout(() => {
            client
                .getDeviceChart(
                    deviceId,
                    { from: range.from, to: range.to },
                    { signal: controller.signal },
                )
                .then((data) => {
                    if (requestId !== latest.current) return;
                    setState({ data, loading: false, error: null });
                })
                .catch((error: unknown) => {
                    // An abort is the expected outcome of changing the range
                    // mid-flight, not a failure worth showing anyone.
                    if (controller.signal.aborted) return;
                    if (requestId !== latest.current) return;

                    setState({
                        data: null,
                        loading: false,
                        error:
                            error instanceof ApiError
                                ? `${error.code}: ${error.message}`
                                : String(error),
                    });
                });
        }, debounceMs);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [deviceId, range.from, range.to, debounceMs]);

    return state;
}
